// CORE-847 §5 — «do not instantly remove the modal; play the reverse animation».
//
// Splits «should be open» into the two things a transition needs: whether the
// pop-up is in the tree at all, and whether it is in its open state. Closing
// flips the second immediately and the first only after the animation has had
// time to run, so the exit is seen instead of cut.
import { useEffect, useState } from 'react';

export interface PopupMount {
  /** Render the pop-up (stays true through the closing animation). */
  mounted: boolean;
  /** Apply the open-state styles (false on the frame it first appears). */
  shown: boolean;
}

export function usePopupMount(open: boolean, closeMs: number): PopupMount {
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    setShown(false);
    const timer = setTimeout(() => setMounted(false), closeMs);
    return () => clearTimeout(timer);
  }, [open, closeMs]);

  // Deliberately a separate effect keyed on `mounted`: the open styles must be
  // applied on the frame AFTER the pop-up first renders closed, or the browser
  // has no previous value to transition from and the whole thing appears at once.
  useEffect(() => {
    if (!mounted || !open) return;
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, [mounted, open]);

  return { mounted, shown };
}
